/**
 * Utilidades para búsqueda de texto: normalización de acentos y búsqueda por tokens.
 * Usado en listados (contratos, contactos, etc.) para mejorar coincidencias.
 */

/**
 * Normaliza un string para comparación en búsqueda:
 * - Minúsculas
 * - Sin acentos (á → a, ñ → n)
 * - Trim
 */
export function normalizeForSearch(text: string | null | undefined): string {
  if (text == null || typeof text !== 'string') return '';
  const trimmed = String(text).trim();
  if (!trimmed) return '';
  return trimmed
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Extrae tokens (palabras) de una query de búsqueda.
 * Normaliza cada token (acentos, minúsculas) y filtra vacíos.
 */
export function searchTokens(query: string | null | undefined): string[] {
  const normalized = normalizeForSearch(query);
  if (!normalized) return [];
  return normalized
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Comprueba si un texto (normalizado) contiene todos los tokens.
 * Útil para búsquedas del tipo "Ana Garcia" → cada palabra debe aparecer en algún sitio.
 */
export function textContainsAllTokens(
  text: string | null | undefined,
  tokens: string[]
): boolean {
  if (tokens.length === 0) return true;
  const normalizedText = normalizeForSearch(text);
  if (!normalizedText) return false;
  return tokens.every((token) => normalizedText.includes(token));
}

/**
 * Comprueba si al menos uno de los campos contiene todos los tokens.
 * Si tokens está vacío, devuelve true.
 */
export function anyFieldContainsAllTokens(
  tokens: string[],
  ...fields: (string | null | undefined)[]
): boolean {
  if (tokens.length === 0) return true;
  const combined = fields
    .map((f) => normalizeForSearch(f))
    .filter(Boolean)
    .join(' ');
  if (!combined) return false;
  return tokens.every((token) => combined.includes(token));
}
